import { Match, getWinningRosterIds } from "model/Match/Match";
import { getDefinedRosterIds } from "model/Roster/Roster";
import { WorldTourSet } from "model/Match/Set/WorldTour/WorldTourSet";

export type WorldTourSeasonRecord = {
  rosterId: string;
  wins: number;
  loses: number;
  rostersBeat: string[];
  tracksWon: number;
  tracksLost: number;
  roundPointsFor: number;
  roundPointsAgainst: number;
  position: number;
};

const createSeasonRecord = (rosterId: string): WorldTourSeasonRecord => {
  return {
    rosterId,
    wins: 0,
    loses: 0,
    rostersBeat: [],
    tracksWon: 0,
    tracksLost: 0,
    roundPointsFor: 0,
    roundPointsAgainst: 0,
    position: 0,
  };
};

export const calculateWorldTourSeasonRecords = (
  rosterIds: string[],
  matches: Match[]
) => {
  const records = new Map<string, WorldTourSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createSeasonRecord(id));
  });

  const completeMatches = matches.filter((m) => m.isComplete);
  for (let i = 0; i < completeMatches.length; i++) {
    const match = completeMatches[i];
    if (match.sets.every((s) => s.type !== "WorldTour")) {
      continue;
    }

    const worldTourSets = match.sets as WorldTourSet[];
    const winnerIds = getWinningRosterIds(match);
    if (!winnerIds) {
      continue;
    }

    const rosterIds = getDefinedRosterIds(match.teams.rosters);
    const loserIds = rosterIds.filter((id) => !winnerIds.includes(id));
    for (let t = 0; t < rosterIds.length; t++) {
      const rosterId = rosterIds[t];
      const rosterRecord = records.get(rosterId);
      if (!rosterRecord) {
        continue;
      }

      if (winnerIds.includes(rosterId)) {
        rosterRecord.wins++;
        rosterRecord.rostersBeat.push(...loserIds);
      } else {
        rosterRecord.loses++;
      }
    }

    worldTourSets.forEach((s) => {
      s.tracks.forEach((t) => {
        const scoreTotal =
          Object.values(t.scores).reduce((a, b) => (a ?? 0) + (b ?? 0), 0) ?? 0;

        for (let r = 0; r < rosterIds.length; r++) {
          const rosterId = rosterIds[r];
          const rosterRecord = records.get(rosterId);
          if (!rosterRecord) {
            continue;
          }

          const rosterRoundPoints = t.scores[rosterId];
          rosterRecord.roundPointsFor += rosterRoundPoints ?? 0;
          rosterRecord.roundPointsAgainst +=
            scoreTotal - (rosterRoundPoints ?? 0);
        }
      });

      const setScoreTotal =
        Object.values(s.scores ?? {}).reduce(
          (a, b) => (a ?? 0) + (b ?? 0),
          0
        ) ?? 0;

      for (let v = 0; v < rosterIds.length; v++) {
        const rosterId = rosterIds[v];
        const rosterRecord = records.get(rosterId);
        if (!rosterRecord) {
          continue;
        }

        const rosterTrackPoints = !!s.scores ? s.scores[rosterId] : undefined;
        rosterRecord.tracksWon += rosterTrackPoints ?? 0;
        rosterRecord.tracksLost += setScoreTotal - (rosterTrackPoints ?? 0);
      }
    });
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (
  a: WorldTourSeasonRecord,
  b: WorldTourSeasonRecord
): number => {
  if (a.wins > b.wins) {
    return -1;
  }

  if (b.wins > a.wins) {
    return 1;
  }

  const aTrackDifferential = a.tracksWon - a.tracksLost;
  const bTrackDifferential = b.tracksWon - b.tracksLost;
  if (aTrackDifferential > bTrackDifferential) {
    return -1;
  }

  if (bTrackDifferential > aTrackDifferential) {
    return 1;
  }

  if (a.rostersBeat.includes(b.rosterId)) {
    return -1;
  }

  if (b.rostersBeat.includes(a.rosterId)) {
    return 1;
  }

  return 0;
};
